/**
 * Pure helpers for the manual-team create/edit dialogs (PRODOPSAI-540).
 * Kept outside the component module so SonarCloud S7721 (helper-inside-render)
 * and S3776 (cognitive complexity) cannot fire on the JSX.
 */

import type {
  ICreateTeamRequest,
  IUpdateTeamRequest,
} from '@skystone/shared';

export const TEAM_KEY_MAX = 50;
export const TEAM_NAME_MAX = 255;
export const REGION_NAME_MAX = 255;

/**
 * Returns the trimmed payload only when both required fields are non-blank and
 * the key contains no whitespace. The backend re-validates everything; this is
 * purely a UI guard for the [Save] button enabled state.
 */
export function canSaveCreate(input: {
  teamKey: string;
  teamName: string;
}): boolean {
  const key = input.teamKey.trim();
  const name = input.teamName.trim();
  if (key.length === 0 || key.length > TEAM_KEY_MAX) return false;
  if (/\s/.test(key)) return false;
  if (name.length === 0 || name.length > TEAM_NAME_MAX) return false;
  return true;
}

export function canSaveUpdate(input: { teamName: string }): boolean {
  const name = input.teamName.trim();
  return name.length > 0 && name.length <= TEAM_NAME_MAX;
}

/** Normalise an optional text input into the wire-shape value (`null` for empty). */
function trimOrNull(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Build the POST /api/admin/teams body. `productId === ''` is treated as
 * "no product" and OMITTED from the wire payload (the backend treats absent
 * productId as null for inserts). A non-empty string is passed through.
 */
export function buildCreateBody(input: {
  teamKey: string;
  teamName: string;
  regionName: string;
  productId: string;
}): ICreateTeamRequest {
  const body: ICreateTeamRequest = {
    teamKey: input.teamKey.trim(),
    teamName: input.teamName.trim(),
    regionName: trimOrNull(input.regionName),
  };
  if (input.productId !== '') {
    body.productId = input.productId;
  }
  return body;
}

/**
 * Build the PATCH /api/admin/teams/:id body. `productId === ''` is an
 * EXPLICIT "no product" intent and is serialised as `null` so the backend
 * unsets the column. A non-empty string is passed through. The two cases
 * diverge from `buildCreateBody` because PATCH needs to distinguish
 * "leave alone" (key absent) from "clear" (key present with null) —
 * but here the dialog always submits a deliberate choice, so we always
 * send the key.
 */
export function buildUpdateBody(input: {
  teamName: string;
  regionName: string;
  productId: string;
}): IUpdateTeamRequest {
  return {
    teamName: input.teamName.trim(),
    regionName: trimOrNull(input.regionName),
    productId: input.productId === '' ? null : input.productId,
  };
}

/**
 * Detect HTTP 409 ConflictError from the api wrapper (team_key collision).
 * The dialog surfaces this inline under the team_key field rather than as a
 * toast so the user can correct the value without dismissing the dialog.
 */
export function isConflictError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const candidate = err as { status?: unknown };
  return candidate.status === 409;
}

/** Detect HTTP 400 ValidationError from the api wrapper. */
export function isValidationError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const candidate = err as { status?: unknown };
  return candidate.status === 400;
}
